import React, {useCallback, useState} from 'react'
import Cropper from 'react-easy-crop'
import {Area, MediaSize} from 'react-easy-crop/types'
import {FormattedMessage} from 'react-intl'
import {captureException} from '../../../errors'
import Dialog from '../../Dialog'
import StyledButton from '../../StyledButton'
import Toast from '../../Toast'
import {getCroppedImg} from './canvasUtils'
import DragAndDrop from './DragAndDrop'

interface Props {
  onCrop: (croppedFile: File) => void
  onCancel: () => void
  open: boolean
}

const minZoom = 0.5

const ImageCropper = ({onCrop, onCancel, open}: Props) => {
  const [crop, setCrop] = useState({x: 0, y: 0})
  const [zoom, setZoom] = useState(minZoom)
  const [showCropper, setShowCropper] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [fileSizeError, setFileSizeError] = useState(false)
  const [imageSizeWarning, setImageSizeWarning] = useState(false)

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  const showCroppedImage = useCallback(async () => {
    try {
      if (imgSrc && croppedAreaPixels) {
        const croppedImage = await getCroppedImg(
          imgSrc,
          croppedAreaPixels,
          imageFile?.name || 'sponsor_image',
          imageFile?.type || 'image/png'
        )

        if (croppedImage) {
          onCrop(croppedImage)
          setShowCropper(false)
        }
        setCrop({x: 0, y: 0})
        setZoom(minZoom)
      }
    } catch (e) {
      captureException(e)
    }
  }, [imgSrc, croppedAreaPixels, imageFile, onCrop])

  const readFile = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise(resolve => {
      const reader = new FileReader()
      reader.addEventListener('load', () => resolve(reader.result), false)
      reader.readAsDataURL(file)
    })
  }

  const onUpload = async (acceptedFiles: Array<File>) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      const fileSize = file.size / 1024 / 1024

      if (fileSize >= 1) {
        setFileSizeError(true)
        return
      }

      setFileSizeError(false)
      setImageFile(file)

      const imageDataUrl = await readFile(file)
      if (imageDataUrl) {
        setImgSrc(imageDataUrl.toString())
        setShowCropper(true)
        // Reset crop position and zoom to initial values
        setCrop({x: 0, y: 0})
        setZoom(minZoom)
      }
    }
  }

  const onMediaLoaded = (media: MediaSize) => {
    setImageSizeWarning(media.naturalWidth < 693 || media.naturalHeight < 390)
  }

  function handleCropCancel() {
    setShowCropper(false)
    setImgSrc(undefined)
    setImageFile(null)
    setFileSizeError(false)
    setImageSizeWarning(false)
    setCrop({x: 0, y: 0})
    setZoom(minZoom)
    onCancel()
  }

  return (
    <Dialog open={open} onClose={handleCropCancel}>
      <div className="m-auto flex max-w-[80%] flex-col items-start space-y-4">
        <Dialog.Title className="typography-headline-5 mx-auto text-teal">
          <FormattedMessage id="manageCommunity.upload_image" />
        </Dialog.Title>

        {imageSizeWarning && (
          <div className="size-full text-center">
            <FormattedMessage id="manageCommunity.image_size_warning" />
          </div>
        )}
        {!showCropper && <DragAndDrop onDrop={onUpload} />}
        {fileSizeError && (
          <div className="w-full [&>div>div]:!w-full [&>div]:!w-full">
            <Toast variant="error">
              <FormattedMessage id="manageCommunity.file_size_warning" />
            </Toast>
          </div>
        )}

        {showCropper && imgSrc && (
          <>
            <div className="relative m-auto h-96 max-h-[50vh] w-full">
              <Cropper
                image={imgSrc}
                crop={crop}
                zoom={zoom}
                minZoom={minZoom}
                aspect={16 / 9}
                onCropChange={setCrop}
                restrictPosition={false}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onMediaLoaded={onMediaLoaded}
                zoomSpeed={0.1}
              />
            </div>
            <input
              type="range"
              className="h-1.5 w-full cursor-pointer appearance-none rounded-lg border-transparent bg-teal"
              value={zoom}
              min={minZoom}
              max={3}
              step={0.1}
              onChange={({target: {value}}) => setZoom(Number(value))}
            />
            <div className="ml-auto flex w-full justify-between">
              <StyledButton onClick={handleCropCancel}>
                <FormattedMessage id="learnerCreator.topbar_button_cancel" />
              </StyledButton>
              <StyledButton onClick={showCroppedImage} disabled={false}>
                <FormattedMessage id="manageCommunity.crop" />
              </StyledButton>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default ImageCropper
